import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { ArrowLeft, Clock, Zap, Heart, Activity, CheckCircle, XCircle, Info } from "lucide-react"
import {
  savePostSession,
  supabase,
  answerActivityPostSession,
  findPostSessionPending,
  getActivityPostSessionAnswer,
  getActivityEndAt,
  formatUnlockTime,
  ACTIVITY_EVENT_TYPES,
  type CalendarEventRow,
} from "@/lib/supabase"
import { computeTrainingLoad } from "@/lib/monitoring/calculations"
import { getPendingSession, savePendingSession, clearPendingSession } from "@/lib/programs"

interface PostSessionFormProps {
  email: string
}

/**
 * Questionnaire d'une activité ajoutée au calendrier (`/post-session?event=<id>`,
 * ouvert depuis la carte du calendrier ou le push de fin + 30 min).
 *   — locked  : avant l'heure de fin prévue ;
 *   — done / skipped : déjà répondu (rempli, ou « Je n'ai pas fait cette séance ») ;
 *   — missing : activité supprimée ou introuvable.
 */
type ActivityState =
  | { kind: "loading" }
  | { kind: "open"; title: string }
  | { kind: "locked"; title: string; endAt: Date }
  | { kind: "done"; title: string }
  | { kind: "skipped"; title: string }
  | { kind: "missing" }

/**
 * Questionnaire ouvert SANS `?event=` (bouton « Post-séance » du Monitoring, push et
 * cloche d'une séance du programme, lien direct). On cherche ce qui attend vraiment
 * une réponse, dans cet ordre :
 *   1. la séance du programme terminée sur cet appareil (localStorage) ;
 *   2. la dernière séance du programme terminée sans questionnaire (ligne partielle) ;
 *   3. une activité du calendrier → bascule en mode activité (`?event=`), qui gère
 *      le verrou « Questionnaire disponible à HHhMM ».
 * Rien → « filled » si la séance de l'étape 1 a bien été remplie, sinon « none ».
 * Jusqu'ici, faute de ligne partielle, la page affichait « déjà enregistré » et un
 * formulaire aux valeurs par défaut, même quand rien n'avait été rempli.
 */
type ProgramState = "loading" | "open" | "filled" | "none"

/**
 * Plage du curseur de durée, en minutes. Activités du calendrier : 15 min à 3 h (un
 * match, une rando, une marche courte) — même plage contrôlée par
 * `answer_activity_post_session`. Séances du programme : inchangé.
 */
const SESSION_DURATION_RANGE = { min: 30, max: 90 }
const ACTIVITY_DURATION_RANGE = { min: 15, max: 180 }
const ACTIVITY_DURATION_TICKS = [
  { value: 15, label: "15 min" },
  { value: 60, label: "1 h" },
  { value: 120, label: "2 h" },
  { value: 180, label: "3 h" },
]

/** Passe en « lue » l'entrée de cloche du questionnaire d'une activité. */
function markActivityNotificationRead(eventId: string) {
  supabase.auth.getUser().then(({ data: { user } }) => {
    if (!user) return
    supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", user.id)
      .eq("type", "post-session")
      .eq("action_url", `/post-session?event=${eventId}`)
      .eq("read", false)
      .then(({ error }) => {
        if (error) console.error("[PostSessionForm] cloche activité :", error)
      })
  })
}

export const PostSessionForm = ({ email: _email }: PostSessionFormProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const eventId = searchParams.get("event")
  const durationRange = eventId ? ACTIVITY_DURATION_RANGE : SESSION_DURATION_RANGE
  const [duration, setDuration] = useState(60)
  const [rpe, setRpe] = useState(5)
  const [pleasure, setPleasure] = useState(7)
  const [submitting, setSubmitting] = useState(false)
  const [program, setProgram] = useState<ProgramState>("loading")
  const [activity, setActivity] = useState<ActivityState>({ kind: "loading" })
  // Saisie proposée seulement quand il y a vraiment quelque chose à évaluer.
  // Activité pas encore terminée : le questionnaire est montré en lecture seule (valeurs
  // par défaut, curseurs grisés, bouton « Disponible à HHhMM »), pour que la page dise ce
  // qui sera demandé au lieu d'être vide. Décision produit du 16/10.
  const readOnly = !!eventId && activity.kind === "locked"
  const formVisible = eventId ? activity.kind === "open" || readOnly : program === "open"

  const trainingLoad = computeTrainingLoad(rpe, duration)

  // ── Activité du calendrier ──
  useEffect(() => {
    if (!eventId) return
    setActivity({ kind: "loading" })
    supabase
      .from("calendar_events")
      .select("id, type, title, event_date, end_time, meta")
      .eq("id", eventId)
      .maybeSingle()
      .then(({ data, error }) => {
        const event = data as Pick<CalendarEventRow, "id" | "type" | "title" | "event_date" | "end_time" | "meta"> | null
        if (error || !event || !ACTIVITY_EVENT_TYPES.includes(event.type)) {
          setActivity({ kind: "missing" })
          return
        }
        const answer = getActivityPostSessionAnswer(event)
        const endAt = getActivityEndAt(event)
        if (answer) setActivity({ kind: answer.status, title: event.title })
        else if (endAt && endAt.getTime() > Date.now()) setActivity({ kind: "locked", title: event.title, endAt })
        else setActivity({ kind: "open", title: event.title })
      })
  }, [eventId])

  // ── Déverrouillage à l'heure de fin, page ouverte : passage en saisie sans recharger ──
  // Minuteur recalé au plus toutes les 60 s (veille du téléphone, changement d'heure),
  // et revérifié au retour au premier plan.
  useEffect(() => {
    if (activity.kind !== "locked") return
    const { endAt, title } = activity
    let timer = 0
    const tick = () => {
      window.clearTimeout(timer)
      const left = endAt.getTime() - Date.now()
      if (left <= 0) setActivity({ kind: "open", title })
      else timer = window.setTimeout(tick, Math.min(left + 250, 60_000))
    }
    const onVisible = () => { if (document.visibilityState === "visible") tick() }
    tick()
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [activity])

  // ── Sans `?event=` : séance du programme, sinon activité (voir ProgramState) ──
  useEffect(() => {
    if (eventId) return
    let cancelled = false
    setProgram("loading")
    ;(async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || cancelled) return

      // 1. Séance terminée sur cet appareil
      let pendingFilled = false
      const pending = getPendingSession()
      if (pending?.session_number) {
        const { data } = await supabase
          .from("session_feedback")
          .select("id, rpe")
          .eq("user_id", user.id)
          .eq("programme_session_number", pending.session_number)
          .eq("programme_id", pending.programme_id ?? null)
          .maybeSingle()
        if (cancelled) return
        if (data && data.rpe === null) { setProgram("open"); return }
        pendingFilled = !!data
      }

      // 2. Dernière séance terminée sans questionnaire (ouverture depuis le push, ou
      //    depuis un autre appareil que celui de la séance), puis 3. activité du
      //    calendrier — même recherche que l'indicateur du bouton « Post-séance ».
      const next = await findPostSessionPending()
      if (cancelled) return
      if (next?.kind === "program") {
        // handleSubmit retrouve la ligne par la séance en attente
        savePendingSession({ programme_id: next.programme_id, session_number: next.session_number })
        setProgram("open")
        return
      }
      if (next?.kind === "activity") {
        navigate(`/post-session?event=${next.eventId}`, { replace: true })
        return
      }

      setProgram(pendingFilled ? "filled" : "none")
    })()
    return () => { cancelled = true }
  }, [eventId, navigate])

  /** Réponse à une activité. `done` false : « Je n'ai pas fait cette séance ». */
  const handleActivityAnswer = async (done: boolean) => {
    if (submitting || !eventId || activity.kind !== "open") return
    setSubmitting(true)
    try {
      const res = await answerActivityPostSession(eventId, done ? { duration, rpe, pleasure } : null)
      if (res.status === "locked") {
        toast.info("Questionnaire pas encore disponible")
        return
      }
      if (res.status === "not_found") {
        setActivity({ kind: "missing" })
        return
      }
      if (res.status === "already_answered") {
        setActivity({ kind: res.answer.status, title: activity.title })
        return
      }
      markActivityNotificationRead(eventId)
      if (done) {
        toast.success("Séance enregistrée !", {
          description: `Charge : ${trainingLoad} UA (RPE ${rpe} × ${duration} min)`,
        })
        navigate("/dashboard/monitoring")
      } else {
        toast.success("C'est noté", { description: "Rien n'est ajouté à ton suivi." })
        navigate("/dashboard/calendar")
      }
    } catch (err) {
      console.error(err)
      toast.error("Erreur", {
        description: err instanceof Error ? err.message : "Sauvegarde impossible",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    if (eventId) return handleActivityAnswer(true)
    if (submitting || program !== "open") return
    setSubmitting(true)
    try {
      const today = new Date().toISOString().split("T")[0]
      const pending = getPendingSession()
      await savePostSession({
        date: today,
        session_duration: duration,
        rpe,
        pleasure,
        training_load: trainingLoad,
        programme_id: pending?.programme_id ?? null,
        programme_session_number: pending?.session_number ?? null,
      })
      clearPendingSession()
      // Plus de consume_trial() ici : la semaine d'essai ne dépend plus jamais des
      // séances (D2). Elle dure 7 jours calendaires, qu'on en fasse une ou quatre.
      toast.success("Séance enregistrée !", {
        description: `Charge : ${trainingLoad} UA (RPE ${rpe} × ${duration} min)`,
      })
      // Mark the most recent unread post-session in-app notif as read (Instagram-like)
      supabase.auth.getUser().then(({ data: { user: u } }) => {
        if (!u) return
        supabase
          .from("notifications")
          .select("id")
          .eq("user_id", u.id)
          .eq("type", "post-session")
          .eq("read", false)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle()
          .then(({ data: notif }) => {
            if (notif) supabase.from("notifications").update({ read: true }).eq("id", notif.id)
          })
      })
      navigate("/dashboard/monitoring")
    } catch (err) {
      console.error(err)
      toast.error("Erreur", {
        description: err instanceof Error ? err.message : "Sauvegarde impossible",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#060a13] cursor-default pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-5 pb-3"
      >
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/dashboard/monitoring")}
            className="flex items-center gap-2 text-gray-400 cursor-pointer hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[14px]">Retour</span>
          </button>
          <span className="text-[11px] uppercase tracking-wider font-semibold text-cyan-400">
            POST-SÉANCE
          </span>
        </div>
        <h1 className="text-[24px] font-bold text-white mb-1">
          Comment s'est passée votre séance ?
        </h1>
        <p className="text-[13px] text-gray-500">
          {eventId && "title" in activity ? activity.title : "Retour sur votre entraînement"}
        </p>
      </motion.div>

      {/* Activité : non réalisée, introuvable. Verrouillée : pas de bandeau, le bouton
          principal dit déjà l'heure (« Disponible à HHhMM »). */}
      {eventId && (activity.kind === "skipped" || activity.kind === "missing") && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="px-5 mb-1">
          <div className="rounded-xl p-3 flex items-center gap-2"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }}>
            <XCircle className="w-4 h-4 text-gray-400 shrink-0" />
            <p className="text-[13px] text-gray-300 font-medium">
              {activity.kind === "skipped" && "Tu as indiqué ne pas avoir fait cette séance"}
              {activity.kind === "missing" && "Cette activité n'existe plus dans ton calendrier"}
            </p>
          </div>
        </motion.div>
      )}

      {/* Already submitted banner — seulement si une réponse existe vraiment */}
      {(eventId ? activity.kind === "done" : program === "filled") && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="px-5 mb-1">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            <p className="text-[13px] text-emerald-300 font-medium">Questionnaire déjà enregistré pour cette séance</p>
          </div>
        </motion.div>
      )}

      {/* Rien à évaluer */}
      {!eventId && program === "none" && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="px-5 mb-1">
          <div className="rounded-xl p-3 flex items-center gap-2"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }}>
            <Info className="w-4 h-4 text-gray-400 shrink-0" />
            <p className="text-[13px] text-gray-300 font-medium">Aucune séance en attente de questionnaire</p>
          </div>
        </motion.div>
      )}

      {formVisible && (<>

      <div className={`transition-opacity duration-300 ${readOnly ? "opacity-40 pointer-events-none select-none" : ""}`} aria-disabled={readOnly || undefined}>

      {/* DURATION */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="px-5 mt-3"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(168,85,247,0.15)" }}>
            <Clock className="w-4 h-4 text-purple-400" />
          </div>
          <span className="text-[14px] font-bold text-white">Durée de la séance</span>
        </div>

        <div className="text-center mb-3">
          <span className="text-[32px] font-extrabold text-white">{duration}</span>
          <span className="text-[12px] text-gray-500 ml-1">min</span>
        </div>

        <div className="relative">
          <div
            className="absolute inset-0 h-1.5 rounded-full top-1/2 -translate-y-1/2"
            style={{ background: "linear-gradient(to right, #a855f7 0%, #6366f1 50%, #06b6d4 100%)" }}
          />
          <input
            type="range" min={durationRange.min} max={durationRange.max} step="5"
            value={duration} onChange={(e) => setDuration(parseInt(e.target.value))}
            disabled={readOnly}
            className="relative w-full h-1.5 bg-transparent appearance-none cursor-pointer disabled:cursor-default postsession-slider"
            style={{ zIndex: 2 }}
          />
        </div>

        {eventId ? (
          // Repères aux valeurs rondes (15 min, 1 h, 2 h, 3 h), placés à leur position
          // réelle : le centre du curseur de 22 px va de 11 px à (largeur − 11 px).
          <div className="relative h-[15px] text-[10px] text-gray-600 mt-2">
            {ACTIVITY_DURATION_TICKS.map(({ value, label }) => {
              const pct = (value - durationRange.min) / (durationRange.max - durationRange.min)
              return (
                <span key={value} className="absolute -translate-x-1/2 whitespace-nowrap"
                  style={{ left: `calc(11px + (100% - 22px) * ${pct})` }}>
                  {label}
                </span>
              )
            })}
          </div>
        ) : (
          <div className="flex justify-between text-[10px] text-gray-600 mt-2 px-1">
            <span>30</span><span>45</span><span>60</span><span>75</span><span>90</span>
          </div>
        )}
      </motion.div>

      {/* RPE */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="px-5 mt-6"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(245,158,11,0.15)" }}>
            <Zap className="w-4 h-4 text-amber-400" />
          </div>
          <span className="text-[14px] font-bold text-white">Difficulté perçue (RPE)</span>
        </div>

        <div className="flex justify-between text-[10px] text-gray-500 px-1 mb-1.5">
          <span>Très facile</span><span>Modéré</span><span>Difficile</span><span>Maximal</span>
        </div>

        <div className="relative">
          <div
            className="absolute inset-0 h-1.5 rounded-full top-1/2 -translate-y-1/2"
            style={{ background: "linear-gradient(to right, #22c55e 0%, #84cc16 25%, #eab308 50%, #f97316 75%, #ef4444 100%)" }}
          />
          <input
            type="range" min="1" max="10" step="1"
            value={rpe} onChange={(e) => setRpe(parseInt(e.target.value))}
            disabled={readOnly}
            className="relative w-full h-1.5 bg-transparent appearance-none cursor-pointer disabled:cursor-default postsession-slider"
            style={{ zIndex: 2 }}
          />
        </div>

        <div className="flex justify-between text-[10px] text-gray-600 mt-2 px-1">
          {[1,2,3,4,5,6,7,8,9,10].map((n) => (
            <span key={n} className={n === rpe ? "text-cyan-400 font-bold" : ""}>{n}</span>
          ))}
        </div>
      </motion.div>

      {/* PLEASURE */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="px-5 mt-6"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(6,182,212,0.15)" }}>
            <Heart className="w-4 h-4 text-cyan-400" />
          </div>
          <span className="text-[14px] font-bold text-white">Plaisir de la séance</span>
        </div>

        <div className="flex justify-between text-[10px] text-gray-500 px-1 mb-1.5">
          <span>Aucun plaisir</span><span>Neutre</span><span>Agréable</span><span>Excellent</span>
        </div>

        <div className="relative">
          <div
            className="absolute inset-0 h-1.5 rounded-full top-1/2 -translate-y-1/2"
            style={{ background: "linear-gradient(to right, #ef4444 0%, #f97316 25%, #eab308 50%, #84cc16 75%, #22c55e 100%)" }}
          />
          <input
            type="range" min="1" max="10" step="1"
            value={pleasure} onChange={(e) => setPleasure(parseInt(e.target.value))}
            disabled={readOnly}
            className="relative w-full h-1.5 bg-transparent appearance-none cursor-pointer disabled:cursor-default postsession-slider"
            style={{ zIndex: 2 }}
          />
        </div>

        <div className="flex justify-between text-[10px] text-gray-600 mt-2 px-1">
          {[1,2,3,4,5,6,7,8,9,10].map((n) => (
            <span key={n} className={n === pleasure ? "text-cyan-400 font-bold" : ""}>{n}</span>
          ))}
        </div>
      </motion.div>

      {/* TRAINING LOAD */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        className="px-5 mt-6"
      >
        <div
          className="rounded-xl p-4 flex items-center justify-between"
          style={{ background: "rgba(12,20,35,0.70)", border: "1px solid rgba(30,41,59,0.55)" }}
        >
          <div>
            <div className="text-[14px] font-bold text-white">Training Load</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Durée × RPE</div>
          </div>
          <div>
            <span className="text-[28px] font-extrabold text-cyan-400">{trainingLoad}</span>
            <span className="text-[12px] text-cyan-400 ml-1 font-semibold">UA</span>
          </div>
        </div>
      </motion.div>

      </div>

      {/* SUBMIT */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="px-5 mt-6"
      >
        <button
          onClick={handleSubmit}
          disabled={submitting || readOnly}
          className="w-full py-4 rounded-xl text-[14px] font-bold text-white tracking-wide cursor-pointer transition-all active:scale-[0.97] disabled:opacity-70 disabled:cursor-default disabled:active:scale-100 flex items-center justify-center gap-2"
          style={{ background: "rgba(12,20,35,0.70)", border: "1px solid rgba(30,41,59,0.55)" }}
        >
          {submitting && (
            <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          )}
          {activity.kind === "locked" && eventId
            ? `Disponible à ${formatUnlockTime(activity.endAt)}`
            : submitting ? "ENREGISTREMENT…" : "ENREGISTRER"}
        </button>
        {eventId && activity.kind === "open" && (
          <button
            onClick={() => handleActivityAnswer(false)}
            disabled={submitting}
            className="w-full mt-2.5 py-3 rounded-xl text-[13px] font-semibold text-gray-400 cursor-pointer transition-all active:scale-[0.97] disabled:opacity-50"
            style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}
          >
            Je n'ai pas fait cette séance
          </button>
        )}
      </motion.div>

      <div className="px-5 mt-3">
        <div
          className="rounded-xl p-3 flex items-start gap-2"
          style={{ background: "rgba(6,182,212,0.05)", border: "1px solid rgba(6,182,212,0.15)" }}
        >
          <Activity className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" />
          <p className="text-[10px] text-gray-400 leading-relaxed">
            La <span className="text-cyan-400 font-semibold">Training Load</span> sert à calculer ton ACWR et ton Strain dans la page Monitoring.
          </p>
        </div>
      </div>
      </>)}

      <style>{`
        .postsession-slider::-webkit-slider-thumb {
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
          border: 2px solid #06b6d4;
        }
        .postsession-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
          border: 2px solid #06b6d4;
        }
      `}</style>
    </div>
  )
}
